import QRCodeStyling from 'qr-code-styling';
import { jsPDF } from 'jspdf';

const mapBody = (body) => {
  switch (body) {
    case 'dot': return 'dots';
    case 'classy': return 'classy';
    case 'classy-rounded': return 'classy-rounded';
    case 'rounded-in': return 'rounded';
    case 'rounded-in-smooth': return 'rounded';
    case 'circular': return 'rounded';
    default: return 'square';
  }
};

const mapEye = (eye) => {
  // qr-code-styling supports 'square' and 'extra-rounded' for corners
  const roundedSet = new Set(['frame2','frame4','frame8','frame10','frame12','frame14']);
  return roundedSet.has(eye) ? 'extra-rounded' : 'square';
};

const mapEyeBall = (eyeBall) => {
  // supports 'square' or 'dot' for inner eye
  const dotSet = new Set(['ball2','ball6','ball11','ball14','ball16','ball19']);
  return dotSet.has(eyeBall) ? 'dot' : 'square';
};

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export async function generateLocalQR({
  data = 'https://example.com',
  config = {},
  size = 600,
  file = 'png',
  logoDataUrl = null,
}) {
  const {
    body = 'square',
    eye = 'frame0',
    eyeBall = 'ball0',
    bodyColor = '#000000',
    bgColor = '#FFFFFF',
    gradientColor1 = null,
    gradientColor2 = null,
    gradientType = 'linear',
    gradientOnEyes = false, // not fully supported; applies only to body
    logoMode = 'default',
  } = config;

  const dotsOptions = {
    type: mapBody(body),
    color: bodyColor,
  };
  if (gradientColor1 && gradientColor2) {
    dotsOptions.gradient = {
      type: gradientType,
      rotation: gradientType === 'linear' ? 0 : 0.5,
      colorStops: [
        { offset: 0, color: gradientColor1 },
        { offset: 1, color: gradientColor2 },
      ],
    };
  }

  const qr = new QRCodeStyling({
    width: size,
    height: size,
    type: 'svg',
    data,
    image: logoDataUrl || undefined,
    imageOptions: {
      crossOrigin: 'anonymous',
      margin: 0,
      hideBackgroundDots: logoMode === 'clean',
    },
    dotsOptions,
    backgroundOptions: { color: bgColor },
    cornersSquareOptions: {
      type: mapEye(eye),
      color: gradientOnEyes && gradientColor1 ? gradientColor1 : (config.eye1Color || bodyColor),
    },
    cornersDotOptions: {
      type: mapEyeBall(eyeBall),
      color: gradientOnEyes && gradientColor2 ? gradientColor2 : (config.eyeBall1Color || bodyColor),
    },
  });

  if (file === 'svg') {
    const blob = await qr.getRawData('svg');
    if (blob instanceof Blob) return await blobToDataUrl(blob);
    // some versions might return string; convert
    return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(blob)))}`;
  }

  // rasterize to png first
  const pngBlob = await qr.getRawData('png');
  const pngDataUrl = await blobToDataUrl(pngBlob);

  if (file === 'pdf') {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: [size + 40, size + 40] });
    doc.addImage(pngDataUrl, 'PNG', 20, 20, size, size);
    const pdfDataUrl = doc.output('datauristring');
    return pdfDataUrl;
  }

  // default PNG
  return pngDataUrl;
}
